/**
 * Outreach API Service
 * Handles all communication with the outreach backend endpoints
 */

import { API_BASE_URL } from '../config/settings';

// Types
export interface OutreachItem {
  id: string;
  post_id: string;
  post_url: string;
  post_title: string | null;
  post_author: string | null;
  post_content: string | null;
  platform: string;
  comment: string;
  reasoning: string | null;
  status: 'pending' | 'posted' | 'failed';
  posted_at: number | null;
  error: string | null;
  quality: 'good' | 'bad' | 'neutral' | null;
  created_at: number;
  updated_at: number;
}

// AI Provider settings
export interface AIProviderSettings {
  provider: 'openai' | 'cloudflare';
  model?: string;
}

// Per-platform settings
export interface PlatformSettings {
  prompt?: string;
  promptId?: string;
  maxPerExecution?: number;
  intervalMinutes?: number;
  maxPerDay?: number;
  enabled?: boolean;
  aiProvider?: AIProviderSettings;
}

export interface OutreachConfig {
  // Default settings
  defaultPrompt: string;
  defaultPromptId?: string;
  defaultMaxPerExecution?: number;
  defaultIntervalMinutes?: number;
  defaultMaxPerDay?: number;
  defaultAIProvider?: AIProviderSettings;

  // Per-platform overrides
  platforms: Record<string, PlatformSettings>;

  // Legacy (backwards compat)
  generationPrompt?: string;
  generationPromptByPlatform?: Record<string, string>;
  maxCommentsPerDay?: number;
}

// Token usage info
export interface TokenUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cost?: number;
}

export interface OutreachListResponse {
  items: OutreachItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface OutreachStats {
  total: number;
  posted: number;
  failed: number;
  pending: number;
  byPlatform: Array<{ platform: string; count: number }>;
  byQuality: Array<{ quality: string; count: number }>;
}

export interface OutreachListParams {
  platform?: string;
  status?: string;
  quality?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// API Functions
export const outreachApi = {
  /**
   * List outreach items with optional filters
   */
  async list(params: OutreachListParams = {}): Promise<OutreachListResponse> {
    const searchParams = new URLSearchParams();
    if (params.platform) searchParams.set('platform', params.platform);
    if (params.status) searchParams.set('status', params.status);
    if (params.quality) searchParams.set('quality', params.quality);
    if (params.search) searchParams.set('search', params.search);
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE_URL}/outreach${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch outreach items');
    }

    return data.data;
  },

  /**
   * Get a single outreach item
   */
  async get(id: string): Promise<OutreachItem> {
    const response = await fetch(`${API_BASE_URL}/outreach/${id}`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch outreach item');
    }

    return data.data;
  },

  /**
   * Update quality rating for an item
   */
  async updateQuality(id: string, quality: 'good' | 'bad' | 'neutral'): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/outreach/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quality })
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to update quality');
    }
  },

  /**
   * Delete an outreach item
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/outreach/${id}`, {
      method: 'DELETE'
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete item');
    }
  },

  /**
   * Get outreach statistics
   */
  async getStats(): Promise<OutreachStats> {
    const response = await fetch(`${API_BASE_URL}/outreach/stats`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch stats');
    }

    return data.data;
  },

  /**
   * Get configuration
   */
  async getConfig(): Promise<OutreachConfig> {
    const response = await fetch(`${API_BASE_URL}/outreach/config`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch config');
    }

    return data.data;
  },

  /**
   * Save configuration
   */
  async saveConfig(config: Partial<OutreachConfig>): Promise<OutreachConfig> {
    const response = await fetch(`${API_BASE_URL}/outreach/config`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(config)
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to save config');
    }

    return data.data;
  },

  /**
   * Export items as JSON (returns blob URL)
   */
  async exportJson(params: { platform?: string; status?: string; quality?: string } = {}): Promise<string> {
    const searchParams = new URLSearchParams();
    if (params.platform) searchParams.set('platform', params.platform);
    if (params.status) searchParams.set('status', params.status);
    if (params.quality) searchParams.set('quality', params.quality);

    const url = `${API_BASE_URL}/outreach/export${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url);
    const blob = await response.blob();

    return URL.createObjectURL(blob);
  },

  // ═══════════════════════════════════════════════════════════════
  // TEST PROMPT METHODS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Run a test with prompt and input
   */
  async testPrompt(params: {
    prompt: string;
    input: string;
    platform?: string;
    promptId?: string;
    model?: string;
  }): Promise<{
    id: string;
    output: string;
    tokens: { input: number; output: number; total: number };
    cost: number;
    model: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/outreach/test-prompt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Test failed');
    }

    return data.data;
  },

  /**
   * Optimize a prompt
   */
  async optimizePrompt(params: {
    prompt: string;
    platform?: string;
    expectedLength?: number;
    tone?: string;
  }): Promise<{
    originalPrompt: string;
    optimizedPrompt: string;
    issues: Array<{
      type: string;
      severity: string;
      description: string;
      suggestion: string;
    }>;
    suggestions: string[];
    improvementSummary: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/outreach/optimize-prompt`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Optimization failed');
    }

    return data.data;
  },

  /**
   * Evaluate an output
   */
  async evaluateOutput(params: {
    input: string;
    output: string;
  }): Promise<{
    score: number;
    passed: boolean;
    feedback: string;
    criteriaScores: Array<{
      name: string;
      score: number;
      feedback: string;
    }>;
  }> {
    const response = await fetch(`${API_BASE_URL}/outreach/evaluate-output`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params)
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Evaluation failed');
    }

    return data.data;
  },

  /**
   * Get test log entries
   */
  async getTestLog(params: { limit?: number; offset?: number } = {}): Promise<{
    entries: Array<{
      id: string;
      timestamp: number;
      prompt: string;
      input: string;
      output: string;
      score?: number;
      passed?: boolean;
      tokens: { input: number; output: number; total: number };
      cost?: number;
      model: string;
    }>;
    total: number;
  }> {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.set('limit', String(params.limit));
    if (params.offset) searchParams.set('offset', String(params.offset));

    const url = `${API_BASE_URL}/outreach/test-log${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch test log');
    }

    return data.data;
  },

  /**
   * Delete a test log entry
   */
  async deleteTestLogEntry(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/outreach/test-log/${id}`, {
      method: 'DELETE'
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to delete entry');
    }
  },

  /**
   * Clear all test log entries
   */
  async clearTestLog(): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/outreach/test-log`, {
      method: 'DELETE'
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to clear log');
    }
  },

  /**
   * Get last test state (for persistence)
   */
  async getTestState(): Promise<{
    prompt: string;
    input: string;
    platform?: string;
    promptId?: string;
  } | null> {
    const response = await fetch(`${API_BASE_URL}/outreach/test-state`);
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to fetch test state');
    }

    return data.data;
  },

  /**
   * Save test state (for persistence)
   */
  async saveTestState(state: {
    prompt: string;
    input: string;
    platform?: string;
    promptId?: string;
  }): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/outreach/test-state`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(state)
    });
    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Failed to save test state');
    }
  }
};
